import { EditIconButton, Form, notify } from '@/components';
import { formatters, ProjectHomeStepDeadlineLookupDto } from '@/core';
import {
  UpdateProjectHomeStepDeadlineArgs,
  useGetProjectHomeStepsQuery,
  useUpdateProjectHomePathStepDeadlineMutation,
} from '@/redux';
import { Button, Modal, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBool } from '@/utils';

interface Props {
  projectId: string;
  homeId: string;
}

const useSubmit = (closeModal: () => void) => {
  const [add] = useUpdateProjectHomePathStepDeadlineMutation();

  return (args: UpdateProjectHomeStepDeadlineArgs) =>
    add(args)
      .unwrap()
      .then(() => {
        closeModal();
        notify.success.t('projects.homeDeadlines.updated');
      });
};

function getNearestUpcomingDeadline(
  steps: ProjectHomeStepDeadlineLookupDto[],
): ProjectHomeStepDeadlineLookupDto | null {
  const upcomingSteps = steps.filter(
    (step) => step.deadlineDate && new Date(step.deadlineDate) > new Date(),
  );

  if (upcomingSteps.length === 0) return null;

  return upcomingSteps.reduce((nearest, current) => {
    const currentDate = new Date(current.deadlineDate!);
    const nearestDate = new Date(nearest.deadlineDate!);
    return currentDate < nearestDate ? current : nearest;
  });
}

export const DeadlineOverrideButton = (props: Props) => {
  const { projectId, homeId } = props;
  const { t } = useTranslation();
  const { data = [] } = useGetProjectHomeStepsQuery({ projectId, homeId });
  const step = getNearestUpcomingDeadline(data);
  const [openModal, setOpenModal] = useBool(false);
  const submit = useSubmit(() => setOpenModal(false));
  const deadlineDate = step?.deadlineDate;

  return (
    step && (
      <Space>
        {deadlineDate && (
          <Typography.Text>
            {t('homes.details.nextDeadline', {
              deadlineDate: formatters.dateTime(deadlineDate, 'date'),
              stepName: step?.name,
            })}
          </Typography.Text>
        )}
        <EditIconButton onClick={() => setOpenModal(true)} />
        {openModal && step && (
          <Modal
            title={t('projects.locationDeadlines.editDeadline')}
            visible
            onCancel={() => setOpenModal(false)}
            footer={[
              <Button
                key="back"
                type="default"
                onClick={() => setOpenModal(false)}
              >
                {t('cancel')}
              </Button>,
              <Form.Submit uid="task-details" key="submit" type="primary">
                {t('save')}
              </Form.Submit>,
            ]}
          >
            <Form.Formik<ProjectHomeStepDeadlineLookupDto>
              uid="task-details"
              i18n="tasks.details"
              initialValues={step}
              onSubmit={submit}
            >
              <Form.Date
                label={t('projects.details.deadlineName', {
                  name: step.name,
                })}
                name={`deadlineDate`}
                allowClear={false}
                required
              />
              <Typography.Text type="secondary">
                {t('homes.details.editDeadlineWarning')}
              </Typography.Text>
            </Form.Formik>
          </Modal>
        )}
      </Space>
    )
  );
};
